import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export interface TransactionItemRow {
  id: string;
  sort: number;
  productId: string | null;
  productSku: string | null;
  productName: string | null;
  name: string;
  /** Per-event category UUID; null for items with no product or deleted category. */
  categoryId: string | null;
  /** Display name; "Custom" fallback. */
  categoryName: string;
  unitPriceCents: number;
  quantity: number;
  lineTotalCents: number;
  allocatedRefundCents: number;
}

/** Items for a transaction. Used by the transaction-detail drawer. */
export function useTransactionItems(transactionId: string | null) {
  return useQuery({
    queryKey: ['transaction', transactionId, 'items'],
    queryFn: () =>
      apiFetch<{ rows: TransactionItemRow[] }>(
        `/api/v1/admin/transactions/${transactionId}/items`,
      ).then((r) => r.rows),
    enabled: Boolean(transactionId),
  });
}

export function useReleaseTransactionInventory(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, note }: { transactionId: string; note?: string }) =>
      apiFetch<{ unitsReleased: number }>(
        `/api/v1/admin/transactions/${transactionId}/inventory/release`,
        {
          method: 'POST',
          body: JSON.stringify(note ? { note } : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'inventory'] });
      qc.invalidateQueries({ queryKey: ['event', eventId, 'reports'] });
    },
  });
}

export interface EmailReceiptResponse {
  id: string;
  receiptEmail: string;
  receiptEmailSentAt: string;
}

/**
 * Trigger Stripe to email the customer their receipt for this transaction.
 * Server validates eligibility (must be a succeeded card/keyed tx with a
 * Stripe payment_intent_id); cash and non-succeeded rows return 4xx.
 */
export function useEmailReceipt() {
  return useMutation({
    mutationFn: ({ transactionId, email }: { transactionId: string; email: string }) =>
      apiFetch<EmailReceiptResponse>(
        `/api/v1/admin/transactions/${transactionId}/email-receipt`,
        {
          method: 'POST',
          body: JSON.stringify({ email }),
        },
      ),
  });
}
